import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Form, Row, Col, Card, Image } from 'react-bootstrap';
import Pagination from 'react-bootstrap/Pagination';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import api from './api/axiosInstance';
import defaultLogo from './assets/img/ic-img.svg';
import icLink from './assets/img/ic-link-square.svg';
import icMail from './assets/img/ic-mail-send.svg';
import icPhone from './assets/img/ic-smart-phone.svg';
import icNoCompanies from './assets/img/ic-empty-box.svg';

const CityJobsCard = ({ cityName, cityId }) => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderStudent, setSortOrderStudent] = useState('asc');
    const [sortOrderIntern, setSortOrderIntern] = useState('asc');
    const [sortOrder13, setSortOrder13] = useState('asc');
    const [sortOrder3, setSortOrder3] = useState('asc');
    const [sortOrderTotal, setSortOrderTotal] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [companiesPerPage, setCompaniesPerPage] = useState(10);

    useEffect(() => {
        if (!cityName) return;
        const fetchJobs = async () => {
            try {
                const res = await api.get('/api/discovery/jobs');
                const allJobs = Array.isArray(res.data) ? res.data : [];
                const cityLower = cityName.trim().toLowerCase();
                const filtered = allJobs.filter(
                    (c) => String(c.city || c.cityRegion || '').trim().toLowerCase() === cityLower
                );
                setCompanies(filtered);
            } catch (error) {
                console.error('Error fetching city jobs:', error);
            }
        };
        fetchJobs();
    }, [cityName]);

    const filtered = companies.filter((c) =>
        String(c.title || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalPages = Math.ceil(filtered.length / companiesPerPage);
    const indexOfLast = currentPage * companiesPerPage;
    const indexOfFirst = indexOfLast - companiesPerPage;
    const currentCompanies = filtered.slice(indexOfFirst, indexOfLast);

    const sort = (getter, setOrder, currentOrder, numeric = false) => {
        const next = currentOrder === 'asc' ? 'desc' : 'asc';
        setOrder(next);
        setCompanies((prev) =>
            [...prev].sort((a, b) => {
                const A = getter(a);
                const B = getter(b);
                if (numeric) return next === 'asc' ? A - B : B - A;
                return next === 'asc'
                    ? String(A).localeCompare(String(B))
                    : String(B).localeCompare(String(A));
            })
        );
    };

    const getPaginationItems = () => {
        if (totalPages <= 1) return [1];
        const range = (s, e) => { const out = []; for (let i = s; i <= e; i++) out.push(i); return out; };
        const b = 1, si = 2;
        const start = range(1, Math.min(b, totalPages));
        const end = range(Math.max(totalPages - b + 1, b + 1), totalPages);
        const sS = Math.max(Math.min(currentPage - si, totalPages - b - si * 2 - 1), b + 2);
        const sE = Math.min(Math.max(currentPage + si, b + si * 2 + 2), totalPages - b - 1);
        const items = [
            ...start,
            ...(sS > b + 2 ? ['ellipsis'] : b + 1 < totalPages - b ? [b + 1] : []),
            ...range(sS, sE),
            ...(sE < totalPages - b - 1 ? ['ellipsis'] : totalPages - b > b ? [totalPages - b] : []),
            ...end,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const renderJobCount = (value) => {
        const n = Number(value) || 0;
        if (!n) return <span className="text-muted">n/a</span>;
        return <span className="fw-bold">{n}</span>;
    };

    return (
        <Card>
            <Card.Body>
                <Row className="mb-3">
                    <Col xs={6}>
                        <h3><span className="item-number">3. </span>Jobs in this city</h3>
                    </Col>
                    <Col xs={6} className="d-flex justify-content-end align-items-center">
                        <Form.Control
                            type="text"
                            placeholder="Search companies..."
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                            style={{ maxWidth: 260 }}
                        />
                    </Col>
                </Row>

                {filtered.length === 0 ? (
                    <Row>
                        <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center py-4">
                            <Image src={icNoCompanies} alt="No Jobs" className="mb-4" />
                            <h2 className="mb-3">No jobs yet.</h2>
                            <p className="mb-0">Jobs assigned to this city will appear here.</p>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row>
                            <Col>
                                <div className="table-responsive list">
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th className="th-fixed-company" onClick={() => sort((c) => c.title || '', setSortOrderName, sortOrderName)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Company
                                                        <span>
                                                            <FaCaretUp className={sortOrderName === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderName === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div>HR contact</div>
                                                </th>
                                                <th>
                                                    <div>Recruiting portal</div>
                                                </th>
                                                <th onClick={() => sort((c) => Number(c.openJobsStudent) || 0, setSortOrderStudent, sortOrderStudent, true)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Student jobs
                                                        <span>
                                                            <FaCaretUp className={sortOrderStudent === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderStudent === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th onClick={() => sort((c) => Number(c.openJobsIntern) || 0, setSortOrderIntern, sortOrderIntern, true)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Intern
                                                        <span>
                                                            <FaCaretUp className={sortOrderIntern === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderIntern === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th onClick={() => sort((c) => Number(c.openJobs13) || 0, setSortOrder13, sortOrder13, true)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        1–3 year
                                                        <span>
                                                            <FaCaretUp className={sortOrder13 === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrder13 === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th onClick={() => sort((c) => Number(c.openJobs3) || 0, setSortOrder3, sortOrder3, true)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        3+ year
                                                        <span>
                                                            <FaCaretUp className={sortOrder3 === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrder3 === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th onClick={() => sort((c) => Number(c.openJobsTotal) || 0, setSortOrderTotal, sortOrderTotal, true)} style={{ cursor: 'pointer' }}>
                                                    <div>
                                                        Total
                                                        <span>
                                                            <FaCaretUp className={sortOrderTotal === 'asc' ? 'text-primary' : ''} />
                                                            <FaCaretDown className={sortOrderTotal === 'desc' ? 'text-primary' : ''} />
                                                        </span>
                                                    </div>
                                                </th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentCompanies.map((company) => (
                                                <tr
                                                    className="company-row discovery-row"
                                                    key={company._id}
                                                    onClick={() => navigate(cityId ? `/city-edit-jobs/${cityId}/company/${company._id}/hiring` : `/company-edit/${company._id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>
                                                        <div>
                                                            <img
                                                                src={company.logo || defaultLogo}
                                                                alt={company.title}
                                                                className="round"
                                                            />
                                                            <span className="text-ellipsis text-ellipsis-country-name">
                                                                <strong>{company.title}</strong>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-inline-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                            {company.hrEmail ? (
                                                                <a href={`mailto:${company.hrEmail}`} className="d-inline-flex align-items-center">
                                                                    <img src={icMail} alt="email" style={{ width: '16px', height: '16px' }} />
                                                                </a>
                                                            ) : null}
                                                            {company.hrPhone ? (
                                                                <a href={`tel:${company.hrPhone}`} className="d-inline-flex align-items-center">
                                                                    <img src={icPhone} alt="phone" style={{ width: '16px', height: '16px' }} />
                                                                </a>
                                                            ) : null}
                                                            {!company.hrEmail && !company.hrPhone ? <span className="text-muted">n/a</span> : null}
                                                        </div>
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        {company.hrRecrutingUrl ? (
                                                            <a
                                                                href={company.hrRecrutingUrl.startsWith('http') ? company.hrRecrutingUrl : `https://${company.hrRecrutingUrl}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="d-inline-flex align-items-center"
                                                            >
                                                                <img src={icLink} alt="recruiting portal" style={{ width: '16px', height: '16px' }} />
                                                            </a>
                                                        ) : (
                                                            <span className="text-muted">n/a</span>
                                                        )}
                                                    </td>
                                                    <td>{renderJobCount(company.openJobsStudent)}</td>
                                                    <td>{renderJobCount(company.openJobsIntern)}</td>
                                                    <td>{renderJobCount(company.openJobs13)}</td>
                                                    <td>{renderJobCount(company.openJobs3)}</td>
                                                    <td>{renderJobCount(company.openJobsTotal)}</td>
                                                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                                        <Link to={cityId ? `/city-edit-jobs/${cityId}/company/${company._id}/hiring` : `/company-edit/${company._id}`} className="btn btn-sm btn-outline-secondary">
                                                            Edit
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>

                        <Row className="pagination-wrapper">
                            <Col className="d-flex align-items-center">
                                <span>Total {filtered.length} {filtered.length === 1 ? 'company' : 'companies'}</span>
                                <Pagination>
                                    <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                                        <BsChevronLeft />
                                    </Pagination.Prev>
                                    {getPaginationItems().map((item, i) =>
                                        item === 'ellipsis' ? (
                                            <Pagination.Ellipsis key={`e-${i}`} disabled />
                                        ) : (
                                            <Pagination.Item key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>
                                                {item}
                                            </Pagination.Item>
                                        )
                                    )}
                                    <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0}>
                                        <BsChevronRight />
                                    </Pagination.Next>
                                </Pagination>
                            </Col>
                            <Col className="pagination-controls">
                                <div className="pagination-controls-per-page">
                                    <Form.Select value={companiesPerPage} onChange={(e) => { setCompaniesPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                        <option value="10">10 / page</option>
                                        <option value="20">20 / page</option>
                                        <option value="40">40 / page</option>
                                    </Form.Select>
                                </div>
                                <label className="pagination-controls-goto-label" htmlFor="city-jobs-goto">Go to</label>
                                <Form.Control
                                    id="city-jobs-goto"
                                    className="pagination-controls-goto-input"
                                    type="number"
                                    value={currentPage}
                                    onChange={(e) => {
                                        const p = Number(e.target.value);
                                        if (p >= 1 && p <= totalPages) setCurrentPage(p);
                                    }}
                                    onBlur={() => {
                                        if (currentPage < 1) setCurrentPage(1);
                                        if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                    }}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default CityJobsCard;
